import { useEffect, useState, useMemo, useCallback } from 'react'
import { connect, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import InfiniteScroll from '../../InfiniteScroll/InfiniteScroll'
import {
  selectGroupFilter,
  getPosts,
  deletePost,
  approvePost,
  selectPosts,
  selectGroups,
  handleLikeFlow,
  createPost,
  updatePost,
  addComment,
  deleteComment,
  selectStatusIsLoadedAllPosts,
  getGroups,
} from '../../../reducers/Organisation/groupsReducer'
import { selectUserData } from '../../../reducers/userDataReducer'
import ConfirmPopup from '../../Popups/ConfirmPopup'
import { selectActiveBuilding, selectBuildings } from '../../../reducers/buildingSelectorReducer'
import { toolbarTabOptions, GROUP_POSTS_FILTERS } from '../helpers'
import { resetQueries } from '../../../utils'
import { GET_POSTS_BASE_QUERY_KEY } from '../../../api/groups/get-posts'
import { GET_PENDING_COMMENTS_QUERY_KEY } from '../../../api/groups/get-pending-comments'
import { useGetPostById, GET_POSTS_BY_ID_QUERY_KEY } from '../../../api/groups/get-post-by-id'

import GroupPostsToolbar from './GroupPostsToolbar/GroupPostsToolbar'
import PostFullCard from './PostFullCard/PostFullCard'
import PostForm from './../GroupPostsDashboard/PostForm/PostForm'
import PostSmallCard from './PostSmallCard/PostSmallCard'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'

const GroupPostsDashboard = ({
  buildings,
  building,
  user,
  getPosts,
  deletePost,
  approvePost,
  handleLikeFlow,
  addComment,
  deleteComment,
  createPost,
  updatePost,
  buildingId,
  groupId,
  groups,
  isLoadedAllPosts,
  getGroups,
}) => {
  const { t } = useTranslation(['common'])
  const { displaySnackbar } = useSnackbar()
  const [isLoading, setLoading] = useState(false)
  const [groupsLoaded, setGroupsLoaded] = useState(false)
  const [isSubmitting, setSubmitting] = useState(false)
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false)
  const [postFormVisible, setPostFormVisible] = useState(false)
  const [fullPostViewVisible, setFullPostViewVisible] = useState(false)
  const [postActionId, setPostActionId] = useState(null)
  const [selectedTab, setSelectedTab] = useState(toolbarTabOptions[0])

  const posts = useSelector((state) => selectPosts(state, groupId))
  const { groupId: paramsGroupId, postId: paramsPostId } = useParams()

  const { data: post, isLoading: isPostLoading } = useGetPostById({
    groupId: paramsGroupId,
    postId: paramsPostId,
  })
  const selectedPostGroup = groups.find((group) => group.id === post?.groupId.id)

  const location = useLocation()
  const navigate = useNavigate()

  const closeFullPost = () => {
    setPostActionId(null)
    setFullPostViewVisible(false)
    navigate('/dashboard/building-dashboard/groups/posts')
  }

  const getPostsData = useCallback(
    async (skip) => {
      setLoading(true)
      try {
        await getPosts(
          buildingId,
          groupId,
          typeof skip !== 'undefined' ? skip : posts.length,
          selectedTab.value === GROUP_POSTS_FILTERS.SCHEDULED ? true : undefined,
        )
      } catch (err) {
        console.error(err)
      } finally {
        setLoading(false)
      }
    },
    [buildingId, groupId, posts.length, selectedTab],
  )

  const fetchGroups = async (id) => {
    await getGroups(id)
    setGroupsLoaded(true)
  }

  const handleDeletePost = (postId) => {
    setPostActionId(postId)
    setDeleteDialogVisible(true)
  }

  const isScheduledPostDelete = useMemo(() => {
    if (!postActionId) return false
    const postToDelete = posts.find((post) => post?.id === postActionId) || post
    return !postToDelete?.published
  }, [postActionId, posts, post])

  const onDeletePost = async () => {
    const groupId = paramsGroupId || posts.find((post) => post.id === postActionId).groupId

    fullPostViewVisible ? setFullPostViewVisible(false) : setLoading(true)

    try {
      await deletePost(groupId, postActionId)
      await getPostsData(0)
    } catch (err) {
      console.error(err)
    } finally {
      closeFullPost()
      setDeleteDialogVisible(false)
      setLoading(false)
      await resetQueries(
        [GET_POSTS_BASE_QUERY_KEY],
        [GET_PENDING_COMMENTS_QUERY_KEY],
        [GET_POSTS_BY_ID_QUERY_KEY],
      )
    }
  }

  const onDeleteComment = async (postId, commentId) => {
    const groupId = paramsGroupId || posts.find((post) => post.id === postId).groupId
    setSubmitting(true)
    try {
      await deleteComment(groupId, postId, commentId)
    } catch (err) {
      console.error(err)
    } finally {
      setSubmitting(false)
      await resetQueries([GET_POSTS_BASE_QUERY_KEY], [GET_PENDING_COMMENTS_QUERY_KEY])
    }
  }

  const onAddComment = async (postId, messageData) => {
    const sendData = {
      message: messageData.message,
    }
    const groupId = paramsGroupId || posts.find((post) => post.id === postId).groupId

    if (messageData.images.length > 0) {
      for (let i = 0; i <= messageData.images.length; i += 1) {
        if (messageData.images[i] !== undefined) {
          sendData[`image${i + 1}`] =
            (messageData.images[i] && messageData.images[i].file) || messageData.images[i]
        }
      }
    }

    setSubmitting(true)
    try {
      await addComment(groupId, postId, sendData)
    } catch (err) {
      console.error(err)
    } finally {
      setSubmitting(false)
      await resetQueries([GET_POSTS_BY_ID_QUERY_KEY])
    }
  }

  const onEditPost = (postId) => {
    setPostActionId(postId)
    setPostFormVisible(true)
  }

  const setFullPostParams = (postId) => {
    const groupId = filteredPosts.find((post) => post.id === postId)?.groupId
    navigate(`${location.pathname}/${postId}/${groupId}`)
  }

  const showFullPost = () => {
    setFullPostViewVisible(true)
    setPostActionId(parseInt(paramsPostId, 10))
  }

  const onManagePostStatus = async (groupId, postId, approved) => {
    fullPostViewVisible ? setSubmitting(true) : setLoading(true)
    try {
      approved ? await approvePost(groupId, postId) : await deletePost(groupId, postId)
      fullPostViewVisible && closeFullPost()
    } catch (err) {
      console.error(err)
    } finally {
      setSubmitting(false)
      setLoading(false)
      await resetQueries([GET_POSTS_BASE_QUERY_KEY], [GET_PENDING_COMMENTS_QUERY_KEY])
    }
  }

  const onLikeDislike = async (groupId, postId, commentId, isLiked) => {
    const likeData = { author: user.user }
    try {
      await handleLikeFlow(isLiked, groupId, postId, commentId, likeData)
      if (fullPostViewVisible) {
        await resetQueries([GET_POSTS_BY_ID_QUERY_KEY])
      }
    } catch (err) {
      console.error(err)
    }
  }

  const groupsOptions = groups.map((group) => {
    return { id: group.id, text: group.name }
  })

  const isScheduledTab = selectedTab.value === GROUP_POSTS_FILTERS.SCHEDULED

  const filteredPosts = (posts || [])
    .filter((post) => {
      if (!post) return false
      const isScheduledPost = !post.published
      return isScheduledTab ? isScheduledPost : !isScheduledPost
    })
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .sort((a, b) => a.approved - b.approved)

  const refetchData = async () => {
    await getPostsData(0)
    await resetQueries([GET_POSTS_BY_ID_QUERY_KEY])
  }

  const getGroupName = (groupId) => {
    if (typeof groupId === 'object') {
      groupId = groupId?.id
    }
    return groups.find((group) => group.id === groupId)?.name || ''
  }

  useEffect(() => {
    setLoading(true)
    setGroupsLoaded(false)
    if (buildingId) {
      fetchGroups(buildingId).catch((err) => {
        displaySnackbar(err.response?.data?.message || t('common:errors.generic'), true)
        setLoading(false)
      })
    }
  }, [buildingId, selectedTab])

  useEffect(() => {
    if (groupsLoaded && (buildingId || groupId)) {
      getPostsData(0)
    }
  }, [buildingId, groupId, groupsLoaded])

  useEffect(() => {
    if (paramsGroupId && paramsPostId) {
      showFullPost()
    }
  }, [paramsGroupId, paramsPostId])

  return (
    <div className="group-posts-dashboard-container">
      <GroupPostsToolbar
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
        buildingId={buildingId}
      />
      <div className={`group-posts-dashboard ${isLoading ? 'is-loading' : ''}`}>
        {filteredPosts && filteredPosts.length && filteredPosts[0] ? (
          <InfiniteScroll
            data={filteredPosts}
            renderItem={(item) => {
              const assignedGroup = groups.find((group) => group.id === item.groupId)

              return (
                <PostSmallCard
                  isSubmitting={isSubmitting || isPostLoading}
                  post={{ ...item, groupId: assignedGroup }}
                  onShowFullPost={setFullPostParams}
                  onDeletePost={handleDeletePost}
                  onEditPost={onEditPost}
                  onManagePostStatus={onManagePostStatus}
                  onLikeDislike={onLikeDislike}
                  currentUser={user.user}
                  groupName={getGroupName(item.groupId)}
                  isCommentsEnabled={!assignedGroup?.disableComments}
                  buildingTimezone={building?.timezone || 'America/Chicago'}
                  onApproveRejectCallback={refetchData}
                />
              )
            }}
            onEndReached={getPostsData}
            isLoadedAll={isLoadedAllPosts}
            style={{ height: '100%' }}
          />
        ) : null}
        {fullPostViewVisible && postActionId && (
          <PostFullCard
            isSubmitting={isSubmitting || isPostLoading}
            onDeletePost={handleDeletePost}
            onEditPost={onEditPost}
            onAddComment={onAddComment}
            onLikeDislike={onLikeDislike}
            onDeleteComment={onDeleteComment}
            onManagePostStatus={onManagePostStatus}
            currentUser={user.user}
            post={post}
            groupName={getGroupName(post?.groupId)}
            buildingTimezone={building?.timezone || 'America/Chicago'}
            handleCancel={closeFullPost}
            isCommentsEnabled={!selectedPostGroup?.disableComments}
            onApproveRejectCallback={refetchData}
          />
        )}
        {postFormVisible && (
          <PostForm
            editPostData={post || filteredPosts.find((post) => post.id === postActionId)}
            createPost={createPost}
            updatePost={updatePost}
            groupsOptions={groupsOptions}
            buildings={buildings}
            buildingId={buildingId}
            buildingTimezone={building?.timezone || 'America/Chicago'}
            onSuccess={() => {
              getPostsData(0)
              fullPostViewVisible && resetQueries([GET_POSTS_BY_ID_QUERY_KEY])
            }}
            handleCancel={() => {
              setPostFormVisible(false)
              if (!fullPostViewVisible) {
                setPostActionId(null)
                setFullPostViewVisible(false)
              }
            }}
          />
        )}
        <ConfirmPopup
          title="Confirmation"
          body={
            isScheduledPostDelete
              ? 'This action will delete the scheduled post. Do you wish to proceed?'
              : 'Are you sure you want to remove this post? Once a post has been deleted, it cannot be restored.'
          }
          cancelText="Cancel"
          confirmText={isScheduledPostDelete ? 'Yes, Delete the Post' : 'Confirm'}
          isOpen={deleteDialogVisible}
          cancelCallback={() => setDeleteDialogVisible(false)}
          confirmCallback={onDeletePost}
        />
      </div>
    </div>
  )
}

GroupPostsDashboard.propTypes = {
  buildingId: PropTypes.number,
  groupId: PropTypes.number,
  groups: PropTypes.array,
  selectedGroupFilter: PropTypes.object,
  user: PropTypes.object,
  buildings: PropTypes.array,
  building: PropTypes.object,
  posts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
      message: PropTypes.string,
      createdAt: PropTypes.number,
      images: PropTypes.arrayOf(PropTypes.string),
      author: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        avatar: PropTypes.string,
        role: PropTypes.string,
      }),
      comments: PropTypes.array,
      groupId: PropTypes.number,
      isWelcomePost: PropTypes.bool,
      likes: PropTypes.array,
      approved: PropTypes.bool,
      scheduledAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  getPosts: PropTypes.func,
  deletePost: PropTypes.func,
  approvePost: PropTypes.func,
  handleLikeFlow: PropTypes.func,
  addComment: PropTypes.func,
  deleteComment: PropTypes.func,
  createPost: PropTypes.func,
  updatePost: PropTypes.func,
  isLoadedAllPosts: PropTypes.bool,
  getGroups: PropTypes.func,
}

export default connect(
  (state) => {
    const building = selectActiveBuilding(state)
    const user = selectUserData(state)
    const selectedGroupFilter = selectGroupFilter(state)
    const groupId = selectedGroupFilter.id !== -1 ? selectedGroupFilter.id : undefined

    return {
      isLoadedAllPosts: selectStatusIsLoadedAllPosts(state),
      groups: selectGroups(state),
      buildings: selectBuildings(state),
      user,
      building,
      buildingId: building ? building.id : null,
      groupId,
    }
  },
  {
    getPosts,
    deletePost,
    approvePost,
    addComment,
    deleteComment,
    handleLikeFlow,
    createPost,
    updatePost,
    getGroups,
  },
)(GroupPostsDashboard)
